import Immutable from 'immutable';
import { getPreference, setPreference } from '../../data/preferences';
import { getEntityTimestamp } from '../../data/entitytimestamps';
import { normalizeResultSet } from '../../data/resultsetutils';

import webapi, {REPORTED_EXCEPTION} from '../../webapi';
import { createModelForState, createModuleState } from '../moduleregistry';
import ResultSetModel from '../resultset/resultsetmodel';
import { processAdvancedFilter, processObjectParameters } from './utils';
import { showMessageBox } from '../../actions/modal';
import { getPhrase } from '../../data/phrases';


const EMPTY_LIST = Immutable.List([]);
const EMPTY_MAP = Immutable.Map({});
const EMPTY_STATE = createModuleState('Search');

let searchSeq = 1;

webapi.registerFunction('search', async function(uri, params, options) {
	/*let params = Object.assign({}, filter || {}, {
		_orderBy,
		firstRow,
		maxRowsPerPage
	});*/
	let res = await this.execute(uri, params);
	if (res === REPORTED_EXCEPTION) {
		return res;
	}
	return normalizeResultSet(res, options);
});

let defaultMaxRowsPerPage = 50;

export function setDefaultMaxRowsPerPage(value) {
	defaultMaxRowsPerPage = value || 50;
}




export default class SearchModel {
	#data;

	constructor(data = null) {
		this.#data = data || EMPTY_MAP;
	}

	setFixedProfile(value) {
		return new SearchModel(this.#data.set('fixedProfile', value));
	}

	getProfile() {
		const data = this.#data;
		return data.get('fixedProfile') || '__default__';
	}

	getPreferenceKeyRoot() {
		const data = this.#data;
		const uri = data.get('uri');
		const profile = data.get('fixedProfile') || '__default__';
		return 'search//' + uri + '//' + profile + '//';

	}

	getPreferenceKeyBase() {
		const customProfile = this.#data.get('customProfile');
		if (customProfile) {
			return this.getPreferenceKeyRoot() + customProfile + '//';
		} else {
			return this.getPreferenceKeyRoot();
		}
	}

	async setCustomProfile(value) {
		setPreference(this.getCustomProfilePreferenceKey(), value);
		let model = new SearchModel(this.#data.set('customProfile', value)).initializePreferences(true);
		return await model.setFirstRow(1).search();
	}

	async deleteCustomProfile(profile) {
		let profiles = (this.#data.get('customProfileList') || EMPTY_LIST).filter(p => p !== profile);
		setPreference(this.getCustomProfileListPreferenceKey(), profiles);
		let model = new SearchModel(this.#data.set('customProfileList', profiles));
		if (profile === this.#data.get('customProfile')) {
			model = await model.setCustomProfile('');
		}
		return model;
	}

	async createCustomProfile(profile) {
		let profiles = (this.#data.get('customProfileList') || EMPTY_LIST);
		if (!profiles.some(p => p === profile)) {
			profiles = profiles.push(profile);
		}
		setPreference(this.getCustomProfileListPreferenceKey(), profiles);
		let model = new SearchModel(this.#data.set('customProfileList', profiles));
		model = await model.setCustomProfile(profile);
		return model;
	}


	getCustomProfilePreferenceKey() {
		return this.getPreferenceKeyRoot() + 'customProfile';
	}

	getCustomProfileListPreferenceKey() {
		return this.getPreferenceKeyRoot() + 'customProfileList';
	}

	getOrderByPreferenceKey() {
		return this.getPreferenceKeyBase() + 'orderBy';
	}

	getOutputPreferenceKey() {
		return this.getPreferenceKeyBase() + 'output';
	}

	getAdvancedFilterPreferenceKey() {
		return this.getPreferenceKeyBase() + 'advancedFilter';
	}

	getCustomFilterPreferenceKey() {
		return this.getPreferenceKeyBase() + 'customFilter';
	}

	getMaxRowsPerPagePreferenceKey() {
		return this.getPreferenceKeyBase() + 'maxRowsPerPage';
	}


	setPagerPrevButtonText(value) {
		return new SearchModel(this.#data.set('pagerPrevButtonText', value));
	}

	setPagerNextButtonText(value) {
		return new SearchModel(this.#data.set('pagerNextButtonText', value));
	}

	setPagerSpread(value) {
		return new SearchModel(this.#data.set('pagerSpread', value));
	}

	initializePreferences(force) {
		let data = this.#data;
		if (!force && data.get('preferencesInitialized')) {
			return this;
		}
		let customProfile = getPreference(this.getCustomProfilePreferenceKey()) || '';
		data = data.set('customProfile', customProfile);
		let customProfileList = getPreference(this.getCustomProfileListPreferenceKey()) || EMPTY_LIST;
		data = data.set('customProfileList', customProfileList);

		const model = new SearchModel(data);

		let output = getPreference(model.getOutputPreferenceKey());
		if (output) {
			data = data.set('output', output);
		} else if (force) {
			data = data.delete('output');
		}
		let advancedFilter = getPreference(model.getAdvancedFilterPreferenceKey());
		if (advancedFilter) {
			data = data.set('advancedFilter', advancedFilter);
		} else if (force) {
			data = data.delete('advancedFilter');
		}
		let customFilter = getPreference(model.getCustomFilterPreferenceKey());
		if (customFilter) {
			data = data.set('customFilter', customFilter);
		} else if (force) {
			data = data.delete('customFilter');
		}
		let orderBy = getPreference(model.getOrderByPreferenceKey());
		if (orderBy) {
			data = data.set('orderBy', orderBy);
		} else if (force) {
			data = data.delete('orderBy');
		}
		let maxRowsPerPage = getPreference(model.getMaxRowsPerPagePreferenceKey());
		if (maxRowsPerPage) {
			data = data.set('maxRowsPerPage', maxRowsPerPage);
		} else if (force) {
			data = data.set('maxRowsPerPage', defaultMaxRowsPerPage);
		}
		data = data.set('preferencesInitialized', true);

		return new SearchModel(data);
	}

	setUri(uri) {
		return new SearchModel(this.#data.set('uri', uri));
	}

	getUri() {
		return this.#data.get('uri');
	}

	setCsvExportCallback(func) {
		return new SearchModel(this.#data.set('csvExportCallback', func));
	}

	setLeftToolbar(func) {
		return new SearchModel(this.#data.set('leftToolbar', func));
	}

	setBottomRightToolbar(func) {
		return new SearchModel(this.#data.set('bottomRightToolbar', func));
	}

	setFixedFilter(params) {
		return new SearchModel(this.#data.set('fixedFilter', Immutable.fromJS(params)));
	}

	setDefaultRowAction(value) {
		return new SearchModel(this.#data.set('defaultRowAction', value));
	}

	setFixedColumns(value) {
		return new SearchModel(this.#data.set('fixedColumns', value));
	}

	getFixedFilter() {
		return (this.#data.get('fixedFilter') || EMPTY_MAP).toJS();
	}

	getNoInitialSearch() {
		return this.#data.get('noInitialSearch');
	}

	setNoInitialSearch(value) {
		return new SearchModel(this.#data.set('noInitialSearch', value));
	}

	setEntityName(entityName) {
		return new SearchModel(this.#data.set('entityName', entityName));
	}

	hasBeenRun() {
		return !!this.#data.getIn(['result', 'data', 'rows']);
	}

	clearResult() {
		if (this.#data.getIn(['result', 'data', 'rows'])) {
			return new SearchModel(this.#data.setIn(['result', 'data'], EMPTY_MAP));
		} else {
			return this;
		}
	}

	setOnBeforeRefresh(func) {
		return new SearchModel(this.#data.set('onBeforeRefresh', func));
	}

	setForm(form) {
		return new SearchModel(this.#data.set('form', form.getState()));
	}

	getForm() {
		return createModelForState(this.#data.get('form'));
	}

	getFilter() {
		const formModel = this.getForm();
		if (!formModel) {
			return {};
		}
		return formModel.getFilter();
	}

	setCustomFilter(value, savePreference) {
		let customFilter = Immutable.fromJS(value);
		if (savePreference) {
			setPreference(this.getCustomFilterPreferenceKey(), customFilter);
		}
		return new SearchModel(this.#data.set('customFilter', customFilter));
	}

	getCustomFilter() {
		let res = this.#data.get('customFilter');
		if (!res) {
			return {};
		}
		return res.toJS();
	}

	getData() {
		return this.#data;
	}

	setOnRowAction(func) {
		return new SearchModel(this.#data.set('onRowAction', func));
	}

	setMaxRowsPerPage(value, savePreference) {
		if (savePreference && value !== this.#data.get('maxRowsPerPage')) {
			setPreference(this.getMaxRowsPerPagePreferenceKey(), value);
		}
		return new SearchModel(this.#data.set('maxRowsPerPage', value));
	}

	focusResult() {
		return new SearchModel(this.#data.set('resultFocusTimestamp', (this.#data.get('resultFocusTimestamp') || 0) + 1));
	}

	setCurrentEntityTimestamp() {
		const entityName = this.#data.get('entityName');
		if (!entityName) {
			return this;
		}
		return new SearchModel(this.#data.set('entityTimestamp', getEntityTimestamp(entityName)));
	}

	setOnIconMouseDown(func) {
		return new SearchModel(this.#data.set('onIconMouseDown', func));
	}

	getEntityTimestamp() {
		return this.#data.get('entityTimestamp');
	}

	setFirstRow(value) {
		return new SearchModel(this.#data.set('firstRow', value));
	}

	getProcessedFilter() {
		return Object.assign({}, processObjectParameters(this.getFilter()), processObjectParameters(this.getCustomFilter()), this.getFixedFilter());
	}

	setAdvancedFilter(filter, savePreference) {
		filter = Immutable.fromJS(filter);
		if (savePreference) {
			setPreference(this.getAdvancedFilterPreferenceKey(), filter);
		}
		return new SearchModel(this.#data.set('advancedFilter', filter));
	}

	setOutput(cols, savePreference) {
		cols = Immutable.fromJS(cols);
		if (savePreference) {
			setPreference(this.getOutputPreferenceKey(), cols);
		}
		return new SearchModel(this.#data.set('output', cols));
	}

	setWithRowCheckBox(value) {
		return new SearchModel(this.#data.set('withRowCheckBox', value));
	}

	updateSearchSeq() {
		return new SearchModel(this.#data.set('searchSeq', searchSeq++));
	}

	async updateRow(pars) {
		if (!pars) {
			return this;
		}
		let filter = Object.assign({}, pars, this.getFixedFilter());
		const output = this.#data.get('output');
		if (output) {
			filter._output = output.toJS();
		}
		filter.firstRow = 1;
		filter.maxRowsPerPage = 1;
		const res = await webapi.search(
			this.#data.get('uri'),
			filter,
			{
				entityName: this.#data.get('entityName')
			}
		);
		if (res === REPORTED_EXCEPTION) {
			return this;
		}
		const row = res.rows[0];
		if (!row) {
			return this;
		}
		let rsm = this.getResultModel();
		const rowIndex = rsm.getRowIndex(pars);
		if (rowIndex < 0) {
			return this;
		}
		rsm = rsm.setRow(rowIndex, Immutable.fromJS(row));
		return this.setResultModel(rsm);
	}

	getFullSearchFilter(process) {
		let filter = this.getProcessedFilter();
		const output = this.#data.get('output');
		if (output) {
			filter._output = output.toJS();
		}
		let orderBy = this.#data.get('orderBy');
		if (!orderBy || orderBy.size === 0) {
			orderBy = this.#data.get('defaultOrderBy');
		}
		if (orderBy) {
			filter._orderBy = orderBy.toArray();
		}
		let advancedFilter = this.#data.get('advancedFilter');
		if (advancedFilter) {
			filter._advancedFilter = processAdvancedFilter(advancedFilter.toJS());
		}
		filter.firstRow = this.#data.get('firstRow') || 1;
		filter.maxRowsPerPage = this.#data.get('maxRowsPerPage') || defaultMaxRowsPerPage;
		if (process) {
			const processFilter = this.#data.get('processFilter');
			if (typeof processFilter === 'function') {
				filter = processFilter(filter);
			}
		}
		return filter;
	}

	setResultSetModuleId(moduleId) {
		return new SearchModel(this.#data.set('resultSetModuleId', moduleId));
	}

	setProcessFilter(value) {
		return new SearchModel(this.#data.set('processFilter', value));
	}

	async search(firstRow) {
		if (typeof firstRow === 'number') {
			return this.setFirstRow(firstRow).search();
		}
		let filter = this.getFullSearchFilter(true);
		const res = await webapi.search(
			this.#data.get('uri'),
			filter,
			{
				entityName: this.#data.get('entityName')
			}
		);
		if (res === REPORTED_EXCEPTION) {
			if (advancedFilter) {
				let dlgRes = await showMessageBox(getPhrase('clearAdvancedFilterOnError'), {
					type: 'confirm'
				});
				if (dlgRes === 'yes') {
					let srch = this.setAdvancedFilter(null, true);
					return await srch.search();
				}
			}
			return this;
		}
		let rsm = new ResultSetModel()
						.setProfile(this.getProfile())
						.setWithRowCheckBox(this.#data.get('withRowCheckBox'))
						.setEntityName(this.#data.get('entityName'))
						.setFixedColumns(this.#data.get('fixedColumns'))
						.setFilter(this.#data.get('advancedFilter'))
						.setSourceUri(this.#data.get('uri'))
						.setResultSet(res);
		let srch = new SearchModel(this.#data.set('result', rsm.getState())).focusResult().setCurrentEntityTimestamp().updateSearchSeq();
		srch.onSearch();
		return srch;
	}

	onSearch() {
		const onSearch = this.#data.get('onSearch');
		if (onSearch) {
			onSearch(this);
		}
	}

	setOnSearch(func) {
		return new SearchModel(this.#data.set('onSearch', func));
	}

	addToOrderBy(columnName, reset, savePreference) {
		let orderBy = this.#data.get('orderBy');
		if (reset || !orderBy || orderBy.size === 0) {
			let singleSortCol;
			if (orderBy && orderBy.size === 1) {
				singleSortCol = orderBy.get(0);
				if (singleSortCol === columnName) {
					columnName = '-' + columnName;
				} else if (singleSortCol === '-' + columnName) {
					columnName = null;
				}
			}
			if (columnName) {
				orderBy = Immutable.List([columnName]);
			} else {
				orderBy = null;
			}
		} else {
			let idx = orderBy.findIndex(col => col === columnName);
			const negCol = '-' + columnName;
			if (idx >= 0) {
				orderBy = orderBy.set(idx, negCol);
			} else {
				idx = orderBy.findIndex(col => col === negCol);
				if (idx >= 0) {
					orderBy = orderBy.set(idx, columnName);
				} else {
					orderBy = orderBy.push(columnName);
				}
			}
		}
		if (savePreference) {
			setPreference(this.getOrderByPreferenceKey(), orderBy);
		}
		return new SearchModel(this.#data.set('orderBy', orderBy));
	}

	getResultModel() {
		return createModelForState(this.#data.get('result'));
	}

	setResultModel(model) {
		return new SearchModel(this.#data.set('result', model.getState()));
	}

	setDefaultOrderBy(value) {
		return new SearchModel(this.#data.set('defaultOrderBy', Immutable.fromJS(value)));
	}

	async refreshIfEntityTimestampChanged() {
		const entityName = this.#data.get('entityName');
		if (!entityName) {
			return this;
		}
		const currEntityTimestamp = getEntityTimestamp(entityName);
		if (currEntityTimestamp > this.getEntityTimestamp()) {
			return this.search();
		} else {
			return this;
		}
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}
